import { FLOW_TYPES, ROUTE_ACTIONS } from "@/helpers/const";
import { openNeoSignupAppSitePurchaseFlow, openNeoSite } from "@/helpers/neoWebsiteHelpers";
import { hasMailSuiteProduct, hasSiteProduct } from "@/helpers/product";
import { partnerMatcher } from "@/helpers/partner";
import router from "@/router";
import {
  canShowRenewOption,
  getDomainName,
  getNeoSignupAppUrl,
  isNeoBrand,
  isNeoCoSiteDomain,
  openURL,
} from "@/helpers";
import bundleEvents from "@/telemetry/bundleEvents";
import { MOBILE_NAV_MENU_TYPE } from "@/const/mobile-nav-bar-constant";

const prefix = "mobile.navbar.sections.";
const redirectTo = path => {
  console.log(path);
  router.push(path);
};

const getIconYPosition = (index, offset = 0, iconHeight = 42) => {
  const position = 0 - (index * iconHeight + offset);
  return {
    "background-position-y": `${position}px`,
  };
};

const { RENEW_BUNDLE, CHANGE_BILLING_CYCLE } = FLOW_TYPES;

export const getSections = ({
  products,
  isMailTrial,
  domainVerifiedAndConnected,
  siteVerifiedAndConnected,
  domainDkimVerified,
  partnerId,
}) => {
  const domainName = getDomainName();
  const _hasMailSuiteProduct = hasMailSuiteProduct(products);
  const _hasSiteProduct = hasSiteProduct(products);
  const _isSquarespacePartner = partnerMatcher.isSquarespacePartner(partnerId);
  const _isNeoCoSiteDomain = isNeoCoSiteDomain();
  const canShowConnectDomainOptions = !_isNeoCoSiteDomain;

  const mailSections = _hasMailSuiteProduct
    ? [
        {
          name: "mail",
          titleKey: prefix + "mail.title",
          items: [
            ...(canShowConnectDomainOptions
              ? [
                  {
                    titleKey: "menuItems.connectDomainToEmail",
                    style: getIconYPosition(9, 17),
                    handler: () => redirectTo("/activate"),
                    isVisible: () => true,
                    right: {
                      type: domainVerifiedAndConnected
                        ? MOBILE_NAV_MENU_TYPE.VERIFIED_BADGE
                        : MOBILE_NAV_MENU_TYPE.PENDING_BADGE,
                    },
                  },
                  {
                    titleKey: "menuItems.emailReputation",
                    style: getIconYPosition(10, 17),
                    handler: () => redirectTo("/email-reputation"),
                    isVisible: () => true,
                    right: {
                      type: domainDkimVerified
                        ? MOBILE_NAV_MENU_TYPE.VERIFIED_BADGE
                        : MOBILE_NAV_MENU_TYPE.PENDING_BADGE,
                    },
                  },
                ]
              : []),
            {
              titleKey: prefix + "buyOrUpgrade.items.emailForNewDomain.title",
              style: getIconYPosition(4, 4),
              handler: () => openURL(getNeoSignupAppUrl(), true),
              isVisible: () => isNeoBrand(),
              right: {
                type: MOBILE_NAV_MENU_TYPE.EXT_LINK,
              },
            },
          ],
        },
      ]
    : [];

  const siteSections = _hasSiteProduct
    ? [
        {
          name: "site",
          titleKey: prefix + "site.title",
          subtitleKey: prefix + "site.subtitle",
          items: [
            {
              titleKey: prefix + "site.items.neoSite.title",
              style: getIconYPosition(0),
              handler: () => openNeoSite(domainName),
              isVisible: () => true,
              right: {
                type: MOBILE_NAV_MENU_TYPE.EXT_LINK,
              },
            },
            ...(canShowConnectDomainOptions
              ? [
                  {
                    titleKey: "menuItems.connectDomainToSite",
                    style: getIconYPosition(11, 19),
                    handler: () => redirectTo("/connect-site-domain"),
                    isVisible: () => true,
                    right: {
                      type: siteVerifiedAndConnected
                        ? MOBILE_NAV_MENU_TYPE.VERIFIED_BADGE
                        : MOBILE_NAV_MENU_TYPE.PENDING_BADGE,
                    },
                  },
                ]
              : []),
            {
              titleKey: prefix + "buyOrUpgrade.items.siteForNewDomain.title",
              style: getIconYPosition(5, 7),
              handler: openNeoSignupAppSitePurchaseFlow,
              isVisible: () => isNeoBrand(),
              right: {
                type: MOBILE_NAV_MENU_TYPE.EXT_LINK,
              },
            },
          ],
        },
      ]
    : [];

  // Hidden for Squarespace, same as the desktop sidebar (SidebarNav/config.js).
  const upgradeAndBillingSections = _isSquarespacePartner
    ? []
    : [
        {
          name: "buy or upgrade",
          titleKey: prefix + "buyOrUpgrade.title",
          items: [
            {
              titleKey: prefix + "buyOrUpgrade.items.buyMailBoxes.title",
              style: getIconYPosition(2),
              handler: () => redirectTo("/buy-email-account"),
              isVisible: () => _hasMailSuiteProduct && !isMailTrial,
            },
            {
              titleKey: prefix + "buyOrUpgrade.items.upgradePlan.title",
              style: getIconYPosition(1),
              handler: () => redirectTo("/billing-and-upgrade"),
              isVisible: () => _hasMailSuiteProduct,
            },
            {
              titleKey: prefix + "buyOrUpgrade.items.upgradeSitePlan.title",
              style: getIconYPosition(1),
              handler: () => redirectTo("/site-billing-and-upgrade"),
              isVisible: () => _hasSiteProduct,
            },
            {
              titleKey: prefix + "buyOrUpgrade.items.getNeoMail.title",
              style: getIconYPosition(2),
              handler: () => redirectTo("/get-mail"),
              isVisible: () => isNeoBrand() && !_hasMailSuiteProduct,
            },
            {
              titleKey: prefix + "buyOrUpgrade.items.getNeoSite.title",
              isExternalLink: _hasSiteProduct,
              style: getIconYPosition(0),
              handler: () => {
                redirectTo({
                  name: "SiteBillingAndUpgrade",
                  query: { action: ROUTE_ACTIONS.GET_SITE },
                });
              },
              isVisible: () => isNeoBrand() && !_hasSiteProduct,
            },
          ],
        },
        {
          name: "billing",
          titleKey: prefix + "billing.title",
          items: [
            {
              titleKey: prefix + "billing.items.mySubscriptions.title",
              style: getIconYPosition(7, 10),
              handler: () => redirectTo("/billing-and-subscription"),
              isVisible: () => true,
            },
            {
              titleKey: prefix + "billing.items.renewSubscription.title",
              style: getIconYPosition(3),
              handler: () => redirectTo(`/billing-and-upgrade?${RENEW_BUNDLE}=true`),
              isVisible: () => canShowRenewOption(),
            },
            {
              titleKey: prefix + "billing.items.changeBillingCycle.title",
              style: getIconYPosition(8, 15),
              handler: () => {
                bundleEvents.logChangeBillingCycleClicked();
                redirectTo(`/mobile-home?${CHANGE_BILLING_CYCLE}=true`);
              },
              isVisible: () => true,
            },
          ],
        },
      ];

  return [
    ...mailSections,
    ...siteSections,
    ...upgradeAndBillingSections,
    {
      name: "more features",
      titleKey: prefix + "moreFeatures.title",
      items: [
        {
          titleKey: prefix + "moreFeatures.items.moreFeatureOnDesktop.title",
          subtitleKey: prefix + "moreFeatures.items.moreFeatureOnDesktop.subTitle",
          style: getIconYPosition(6, 0, 54),
          hideRedirectIcon: true,
          isVisible: () => true,
        },
      ],
    },
  ];
};
